'use client';

import { useEffect, useState } from 'react';
import styles from './ReviewsList.module.scss';
import { useTranslations } from 'next-intl';

interface Review {
  id: string;
  documentId: string;
  authorName: string;
  rating: number;
  title: string;
  content: string;
  createdAt: string;
  helpful: number;
  notHelpful: number;
}

interface ReviewsListProps {
  parkId: string;
  parkType: 'bikepark' | 'pumptrack' | 'snowpark';
}

export default function ReviewsList({ parkId, parkType }: ReviewsListProps) {
  const t = useTranslations('reviews');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);

  useEffect(() => {
    async function fetchReviews() {
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}/api/reviews?filters[${parkType}][documentId][$eq]=${parkId}&filters[status][$eq]=approved&sort=createdAt:desc`
        );

        if (response.ok) {
          const data = await response.json();
          setReviews(data.data || []);

          // Calculate average rating
          if (data.data && data.data.length > 0) {
            const sum = data.data.reduce((acc: number, review: any) => acc + review.rating, 0);
            setAverageRating(Math.round((sum / data.data.length) * 10) / 10);
          }
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchReviews();
  }, [parkId, parkType]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  if (isLoading) {
    return (
      <div className={styles.reviewsList}>
        <div className={styles.loading}>{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className={styles.reviewsList}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t('title', { count: reviews.length })}
        </h3>
        {reviews.length > 0 && (
          <div className={styles.averageRating}>
            <span className={styles.stars}>{renderStars(Math.round(averageRating))}</span>
            <span className={styles.ratingValue}>{averageRating}/5</span>
            <span className={styles.reviewCount}>{t('basedOn', { count: reviews.length })}</span>
          </div>
        )}
      </div>

      {reviews.length === 0 ? (
        <div className={styles.noReviews}>
          <p>{t('noReviews')}</p>
        </div>
      ) : (
        <div className={styles.reviews}>
          {reviews.map((review) => (
            <div key={review.documentId} className={styles.review}>
              <div className={styles.reviewHeader}>
                <div className={styles.reviewAuthor}>
                  <span className={styles.authorName}>{review.authorName}</span>
                  <span className={styles.reviewDate}>{formatDate(review.createdAt)}</span>
                </div>
                <div className={styles.reviewRating}>
                  <span className={styles.stars}>{renderStars(review.rating)}</span>
                </div>
              </div>
              <h4 className={styles.reviewTitle}>{review.title}</h4>
              <p className={styles.reviewContent}>{review.content}</p>
              {/* Optional: Helpful buttons
              <div className={styles.reviewActions}>
                <button className={styles.helpfulButton}>
                  👍 Pomocna ({review.helpful})
                </button>
                <button className={styles.helpfulButton}>
                  👎 Niepomocna ({review.notHelpful})
                </button>
              </div>
              */}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
